import { useEffect, useState } from 'react';
import { useAppSelector } from '@store/hooks';

/**
 * Default access-token lifetime used to derive the bar's full width.
 *
 * Mirrors the API's ``ACCESS_TOKEN_EXPIRE_MINUTES`` (default 60). We can't
 * recover the exact issuedAt for a token already in localStorage so we
 * treat the original lifetime as a constant; the bar fills back to 100%
 * on every successful refresh, which is the user-facing contract.
 */
export const SESSION_TOTAL_MS = 60 * 60 * 1000;

export interface SessionCountdown {
  /** Active when the user is logged in and we know an expiry timestamp. */
  isActive: boolean;
  /** Milliseconds until ``expiresAt``. Clamped to ``[0, SESSION_TOTAL_MS]``. */
  remainingMs: number;
  /** Full session duration used as the bar's denominator. */
  totalMs: number;
  /** ``remainingMs / totalMs`` clamped to ``[0, 1]``. */
  percentage: number;
  /** HSL color interpolated from green (full) through yellow to orange (zero). */
  color: string;
  /** Raw ``expiresAt`` from Redux (or null when unknown). */
  expiresAt: number | null;
}

function colorForPercent(pct: number): string {
  // Hue 30 = orange, 120 = green. Interpolate linearly: empty -> orange, full -> green.
  const clamped = Math.max(0, Math.min(1, pct));
  const hue = 30 + 90 * clamped;
  return `hsl(${hue}, 75%, 45%)`;
}

/**
 * Read-only Redux-backed countdown hook used by the header session bar
 * and the 5-minute warning modal. Re-renders once a second while the
 * session is active so callers don't need their own timers.
 */
export function useSessionCountdown(): SessionCountdown {
  const expiresAt = useAppSelector((state) => state.auth.tokens?.expiresAt ?? null);
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);

  const [now, setNow] = useState<number>(() => Date.now());

  useEffect(() => {
    if (!isAuthenticated || expiresAt == null) {
      return;
    }
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [expiresAt, isAuthenticated]);

  const isActive = isAuthenticated && expiresAt != null;
  const totalMs = SESSION_TOTAL_MS;
  const remainingMs = isActive
    ? Math.max(0, Math.min(totalMs, (expiresAt as number) - now))
    : 0;
  const percentage = isActive ? remainingMs / totalMs : 0;

  return {
    isActive,
    remainingMs,
    totalMs,
    percentage,
    color: colorForPercent(percentage),
    expiresAt,
  };
}
